import { Grid, Hidden } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom';
import { ContentSpacer } from '../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageContainer } from '../../global/components/layout/page-container';
import { PageHeader } from '../../global/components/layout/page-header';
import { TcsmModal } from '../../global/components/tcsm-modal';
import { buttonStyles } from '../../global/constants/theme/styles/components/buttonstyles';
import { searchPageStyles } from '../../global/constants/theme/styles/pages/search/search-page-layout';
import { sortTableStyles } from '../../global/constants/theme/styles/tables/generic-sort-table';
import { useBasePath } from '../../global/hooks/useBasePath';
import useWindowDimensions from '../../global/hooks/useWindowSize';
import { ArcRoutes } from '../../pages/arc/constants/routes';
import { GroupsState } from '../../pages/arc/features/groups/groups-model';
import { SearchProps } from '../../pages/arc/models/arc-epi-props';
import { TcsmStates } from '../../root/tcsm-model';
import { BackendPagination } from '../components/backend-pagination';
import { BackendSearchList } from '../components/backend-search-list';
import { CheckBoxFilter } from '../components/checkbox-filter';
import { DateTypeFilter } from '../components/date-type-filter';
import { FilterActionButtons } from '../components/filter-actions-buttons';
import { FilterPills } from '../components/filter-pills';
import { BackendSearchState, FilterTypes, SearchApp, SearchAppContext } from '../models/search-models';
import { resetSearchByApp } from '../slices/backend-search-slice';
import { BenefitGroupChangeLogItemView } from './benefit-group-change-log-item';
import { BenefitGroupChangeLogLabels } from './benefit-group-change-log-labels';
import { updateBenefitGroupChangeLogList } from './benefit-group-change-log-requests';
import { ChangeLogModalContent } from './change-log-modal-content';

export function BenefitGroupChangeLogSearch() {

	const groupsState = useSelector<TcsmStates, GroupsState>(state => state.groupsState);
	const groupSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.GroupSearch}`);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state?.epiProps?.searchSettingsState?.epiContent);
	const { state } = useLocation<any>();
	const routeMatch = useRouteMatch();
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [modalContent, setModalContent] = useState(null);
	const buttonClasses = buttonStyles();
	const pageClasses = searchPageStyles();
	const tableClasses = sortTableStyles();
	const windowDimension = useWindowDimensions();
	const desktopScreenSize = windowDimension?.width >= 1024;
	const [expand, setExpand] = useState(desktopScreenSize);
	const [firstExpand, setFirstExpand] = useState(true);

	useEffect(() => {
		return function cleanUp() {
			dispatch(resetSearchByApp(SearchApp.BENEFIT_GROUP_CHANGE_LOG));
		};
	}, []);


	if (!groupsState?.currentGroup?.group_id) {
		return (
			<Redirect to={{
				pathname: groupSelect,
				state: {
					actionLink: routeMatch.path,
					finalAction: state?.finalAction,
				},
			}} />);
	}


	const setExpandAction = () => {
		setExpand(!expand);
		setFirstExpand(!expand);
	};


	return (
		<SearchAppContext.Provider value={SearchApp.BENEFIT_GROUP_CHANGE_LOG}>
			<PageContainer>
				<MainContent fullWidth>
					<TcsmModal
						open={open}
						handleClose={() => setOpen(false)}
					>
						<ChangeLogModalContent item={modalContent} />
					</TcsmModal>
					<PageHeader
						headerText={searchSettings?.benefitGroupChangeLogTitle}
						description={searchSettings?.benefitGroupChangeLogDescription}
					/>
					<Grid container alignItems='flex-start'>
						<Grid className={pageClasses.searchFilters} container item md={12} lg={3}>
							<Grid className='filters-header' item sm={12} container justifyContent='space-between' alignItems='baseline'>

								<h2>{searchSettings?.filterSectionTitle}</h2>
								<button className={buttonClasses.linkButton} type='button' onClick={setExpandAction} ><strong>{expand ? searchSettings?.collapseFilterText : searchSettings?.showAllFilterText}</strong></button>
							</Grid>
							<FilterPills />
							<div className='filters-tablet-columns'>
								<div className='filters-tablet-columns-item'>
									<DateTypeFilter filterTitle={searchSettings?.submittedDateFilterTitle} expand={firstExpand} hideType dateLabel={searchSettings?.dateFilterLabel} />
									<CheckBoxFilter
										filterType={FilterTypes.changeMadeFor}
										filterTitle={searchSettings?.changeMadeForFilterTitle}
										filterHelperText={searchSettings?.changeMadeForHelperText}
										expand={expand}
									/>
									<CheckBoxFilter
										filterType={FilterTypes.requestBy}
										filterTitle={searchSettings?.requestByFilterTitle}
										filterHelperText={searchSettings?.requestByFilterHelperText}
										expand={expand}
									/>
								</div>
								<div className='filters-tablet-columns-item'>
									<CheckBoxFilter
										filterType={FilterTypes.status}
										filterTitle={searchSettings?.statusFilterTitle}
										filterHelperText={searchSettings?.statusHelperText}
										expand={expand}
									/>
									<CheckBoxFilter
										filterType={FilterTypes.eventType}
										filterTitle={searchSettings?.eventTypeFilterLabel}
										filterHelperText={searchSettings?.eventTypeFilterHelperText}
										expand={expand}
									/>
									<CheckBoxFilter
										filterType={FilterTypes.changeType}
										filterTitle={searchSettings?.changeTypeFilterTitle}
										filterHelperText={searchSettings?.changeTypeFilterHelperText}
										expand={expand}
									/>
									<FilterActionButtons />
								</div>
							</div>
						</Grid>
						<Grid item xs>
							<ContentSpacer>
								<Grid item sm={12} container justifyContent='space-between' alignItems='baseline' spacing={1}>
									<Grid item>
										<p>{searchSettings?.filterOptionHelperText}</p>
									</Grid>
								</Grid>
							</ContentSpacer>
							<div className={tableClasses.sortTable}>
								<Hidden mdDown>
									<BenefitGroupChangeLogLabels />
								</Hidden>
								<div className='sort-table-content'>
									<BackendSearchList
										updateList={(searchState: BackendSearchState) => updateBenefitGroupChangeLogList(searchState, searchSettings?.eventTypeMappingBenefitsGroup, dispatch, groupsState?.currentGroup?.group_id)}
										emptyResultMessage={searchSettings.benefitGroupChangeLogEmptyResult}
										renderItem={(item) =>
											<BenefitGroupChangeLogItemView
												key={`${item?.id}`}
												item={item}
												setContent={setModalContent}
												setOpen={setOpen}
												settings={searchSettings}
											/>}
									/>
									<BackendPagination />
								</div>
							</div>
						</Grid>
					</Grid>
				</MainContent>
			</PageContainer>
		</SearchAppContext.Provider>
	);
}

export default BenefitGroupChangeLogSearch;